import React, { useEffect, useState } from 'react';
import { getCatalog, createCatalogItem, updateCatalogItem, cleanupContainerMbls, findDuplicateShipments, removeDuplicateShipments, migrateStatusCatalogs } from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Loader2, Wrench, Sparkles, Trash2, Search, RefreshCw } from 'lucide-react';

const CATALOG_KEYS = ['shipment_status', 'container_status', 'event_types', 'incoterms'];

export default function CatalogsPage() {
  const { t } = useI18n();
  const CATALOG_TYPES = CATALOG_KEYS.map(key => ({
    key,
    label: {
      shipment_status: t('catalogs.shipmentStatus'),
      container_status: t('catalogs.containerStatus'),
      event_types: t('catalogs.eventTypes'),
      incoterms: t('catalogs.incoterms'),
    }[key] || key,
  }));

  const [activeTab, setActiveTab] = useState('shipment_status');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', label: '', sort_order: 0, is_active: true });
  const [saving, setSaving] = useState(false);
  const [cleaningMbls, setCleaningMbls] = useState(false);
  const [findingDuplicates, setFindingDuplicates] = useState(false);
  const [removingDuplicates, setRemovingDuplicates] = useState(false);
  const [duplicatesInfo, setDuplicatesInfo] = useState(null);
  const [migratingStatuses, setMigratingStatuses] = useState(false);

  useEffect(() => {
    loadCatalog();
  }, [activeTab]);

  const loadCatalog = async () => {
    setLoading(true);
    try {
      const res = await getCatalog(activeTab, true);
      setItems(res.data);
    } catch (err) {
      toast.error(t('catalogs.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.label.trim()) {
      toast.error(t('common.codeAndLabelRequired'));
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await updateCatalogItem(activeTab, editing.code, form);
        toast.success(t('common.updatedOk'));
      } else {
        await createCatalogItem(activeTab, form);
        toast.success(t('common.createdOk'));
      }
      setShowForm(false);
      setEditing(null);
      setForm({ code: '', label: '', sort_order: 0, is_active: true });
      loadCatalog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({ code: item.code, label: item.label, sort_order: item.sort_order, is_active: item.is_active });
    setShowForm(true);
  };

  const handleNewItem = () => {
    setEditing(null);
    setForm({ code: '', label: '', sort_order: items.length * 10, is_active: true });
    setShowForm(true);
  };

  const handleCleanupMbls = async () => {
    if (!confirm('¿Deseas ejecutar la limpieza de MBLs? Esto corregirá espacios y caracteres especiales en todos los embarques y contenedores.')) {
      return;
    }
    setCleaningMbls(true);
    try {
      const res = await cleanupContainerMbls();
      toast.success(`Limpieza completada: ${res.data.containers_fixed} contenedores y ${res.data.shipments_fixed} embarques corregidos`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ejecutar limpieza');
    } finally {
      setCleaningMbls(false);
    }
  };

  const handleFindDuplicates = async () => {
    setFindingDuplicates(true);
    try {
      const res = await findDuplicateShipments();
      setDuplicatesInfo(res.data);
      if (res.data.total_duplicate_groups === 0) {
        toast.success('No se encontraron embarques duplicados');
      } else {
        toast.info(`Se encontraron ${res.data.total_duplicate_groups} grupos de duplicados`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al buscar duplicados');
    } finally {
      setFindingDuplicates(false);
    }
  };

  const handleRemoveDuplicates = async () => {
    if (!confirm('¿Deseas eliminar los embarques duplicados? Se mantendrá el registro más completo y se eliminarán los demás. Los contenedores se fusionarán.')) {
      return;
    }
    setRemovingDuplicates(true);
    try {
      const res = await removeDuplicateShipments();
      toast.success(`Eliminación completada: ${res.data.duplicates_removed} duplicados eliminados, ${res.data.containers_merged} contenedores fusionados`);
      setDuplicatesInfo(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar duplicados');
    } finally {
      setRemovingDuplicates(false);
    }
  };

  // Migrate status catalogs to the new format
  const handleMigrateStatuses = async () => {
    if (!confirm('¿Deseas actualizar los catálogos de estatus al nuevo formato?\n\nEsto reemplazará los estatus actuales con los 12 nuevos estatus de embarque y 11 de contenedor.\n\nLos datos existentes serán migrados automáticamente.')) {
      return;
    }
    setMigratingStatuses(true);
    try {
      const res = await migrateStatusCatalogs();
      const r = res.data.results;
      toast.success(`Migración completada: ${r.shipment_statuses_updated} estatus de embarque, ${r.container_statuses_updated} estatus de contenedor, ${r.shipments_migrated + r.containers_migrated} registros actualizados`);
      loadCatalog(); // Reload current catalog
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al migrar estatus');
    } finally {
      setMigratingStatuses(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="catalogs-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl">{t('catalogs.title')}</h1>
            <p className="text-muted-foreground">{t('catalogs.subtitle')}</p>
          </div>
          <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) { setEditing(null); setForm({ code: '', label: '', sort_order: 0, is_active: true }); } }}>
            <DialogTrigger asChild>
              <Button onClick={handleNewItem} data-testid="new-catalog-item-btn">
                <Plus size={16} className="mr-2" />
                {t('catalogs.addItem')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? t('catalogs.editItem') : t('catalogs.addItem')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Código *</Label>
                  <Input
                    value={form.code}
                    onChange={(e) => setForm({...form, code: e.target.value.toUpperCase().replace(/\s/g, '_')})}
                    placeholder="Ej: EN_TRANSITO"
                    className="mt-1 font-mono"
                    disabled={!!editing}
                    data-testid="catalog-code-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('catalogs.codeHelp')}</p>
                </div>
                <div>
                  <Label>{t('catalogs.label')} *</Label>
                  <Input
                    value={form.label}
                    onChange={(e) => setForm({...form, label: e.target.value})}
                    placeholder="Ej: En Tránsito"
                    className="mt-1"
                    data-testid="catalog-label-input"
                  />
                </div>
                <div>
                  <Label>{t('catalogs.order')}</Label>
                  <Input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({...form, sort_order: parseInt(e.target.value) || 0})}
                    className="mt-1 w-24"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(v) => setForm({...form, is_active: v})}
                  />
                  <Label>{t('users.active')}</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
                  <Button type="submit" disabled={saving} data-testid="save-catalog-item-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-card border border-border">
            {CATALOG_TYPES.map(cat => (
              <TabsTrigger key={cat.key} value={cat.key} data-testid={`tab-${cat.key}`}>
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {CATALOG_TYPES.map(cat => (
            <TabsContent key={cat.key} value={cat.key}>
              <div className="bg-card border border-border rounded-sm overflow-hidden">
                <table className="w-full dense-table">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                      <th className="py-2 px-3 font-medium w-12">{t('catalogs.order')}</th>
                      <th className="py-2 px-3 font-medium">{t('catalogs.code')}</th>
                      <th className="py-2 px-3 font-medium">{t('catalogs.label')}</th>
                      <th className="py-2 px-3 font-medium">{t('common.status')}</th>
                      <th className="py-2 px-3 font-medium w-20">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t('common.loading')}</td></tr>
                    ) : items.length === 0 ? (
                      <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t('catalogs.noItems')}</td></tr>
                    ) : (
                      items.map((item) => (
                        <tr key={item.code} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`catalog-item-${item.code}`}>
                          <td className="py-2 px-3 text-sm text-muted-foreground">{item.sort_order}</td>
                          <td className="py-2 px-3 font-mono text-sm">{item.code}</td>
                          <td className="py-2 px-3">{item.label}</td>
                          <td className="py-2 px-3">
                            <span className={`text-xs px-2 py-0.5 rounded ${item.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                              {item.is_active ? t('users.active') : t('users.inactive')}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <Button size="icon" variant="ghost" onClick={() => handleEdit(item)}>
                              <Edit2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Maintenance Tools */}
        <div className="mt-8 bg-card border border-border rounded-sm p-4">
          <div className="flex items-center gap-2 mb-4">
            <Wrench size={20} className="text-amber-400" />
            <h2 className="font-heading text-lg">{t('catalogs.maintenance')}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-secondary/30 rounded-sm p-4">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Sparkles size={16} className="text-blue-400" />
                {t('catalogs.cleanMbls')}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {t('catalogs.cleanMblsDesc')}
              </p>
              <Button 
                variant="outline" 
                onClick={handleCleanupMbls}
                disabled={cleaningMbls}
                data-testid="cleanup-mbls-btn"
              >
                {cleaningMbls ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('catalogs.cleaning')}
                  </>
                ) : (
                  <>
                    <Sparkles size={14} className="mr-2" />
                    {t('catalogs.runCleanup')}
                  </>
                )}
              </Button>
            </div>

            <div className="bg-secondary/30 rounded-sm p-4">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Trash2 size={16} className="text-red-400" />
                {t('catalogs.removeDuplicatesTitle')}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {t('catalogs.removeDuplicatesDesc')}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={handleFindDuplicates}
                  disabled={findingDuplicates}
                  data-testid="find-duplicates-btn"
                >
                  {findingDuplicates ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      {t('catalogs.searching')}
                    </>
                  ) : (
                    <>
                      <Search size={14} className="mr-2" />
                      {t('catalogs.findDuplicates')}
                    </>
                  )}
                </Button>
                {duplicatesInfo && duplicatesInfo.total_duplicate_groups > 0 && (
                  <Button 
                    variant="destructive" 
                    onClick={handleRemoveDuplicates}
                    disabled={removingDuplicates}
                    data-testid="remove-duplicates-btn"
                  >
                    {removingDuplicates ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('catalogs.removing')}
                      </>
                    ) : (
                      <>
                        <Trash2 size={14} className="mr-2" />
                        {t('catalogs.removeDuplicates')} ({duplicatesInfo.total_duplicate_groups})
                      </>
                    )}
                  </Button>
                )}
              </div>
              {duplicatesInfo && (
                <div className="mt-3 text-sm">
                  {duplicatesInfo.total_duplicate_groups === 0 ? (
                    <p className="text-emerald-400">{t('catalogs.noDuplicates')}</p>
                  ) : (
                    <div className="space-y-1">
                      <p className="text-amber-400">{t('catalogs.duplicatesFound')}: {duplicatesInfo.total_duplicate_groups}</p>
                      <div className="max-h-32 overflow-y-auto bg-secondary/50 rounded p-2 text-xs font-mono">
                        {duplicatesInfo.duplicates.slice(0, 10).map((dup, idx) => (
                          <div key={idx} className="text-muted-foreground">
                            {dup.clean_mbl} ({dup.count} {t('catalogs.records')})
                          </div>
                        ))}
                        {duplicatesInfo.duplicates.length > 10 && (
                          <div className="text-muted-foreground">... +{duplicatesInfo.duplicates.length - 10}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-secondary/30 rounded-sm p-4 md:col-span-2 border-2 border-amber-500/30">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <RefreshCw size={16} className="text-amber-400" />
                {t('catalogs.migrateStatuses')}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {t('catalogs.migrateStatusesDesc')}
              </p>
              <Button 
                variant="default"
                className="bg-amber-600 hover:bg-amber-700"
                onClick={handleMigrateStatuses}
                disabled={migratingStatuses}
                data-testid="migrate-statuses-btn"
              >
                {migratingStatuses ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('catalogs.migrating')}
                  </>
                ) : (
                  <>
                    <RefreshCw size={14} className="mr-2" />
                    {t('catalogs.migrateNow')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
